'use client'
import type { ReactNode } from 'react'
import { GrandSelect, type GrandSelectProps } from 'ui/common/GrandSelect'
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'
import {
  FormDescription,
  FormField,
  FormFieldControl,
  FormFieldLabel,
  FormMessage
} from '../FormField'

interface SelectOption {
  value: string | number
  label: string
  [key: string]: unknown
}

interface GrandFormSelectFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false
> {
  name: TName
  label?: string
  description?: string
  tooltip?: ReactNode
  placeholder?: string
  options: Option[]
  required?: boolean
  optional?: boolean
  disabled?: boolean
  isClearable?: boolean
  isSearchable?: boolean
  isLoading?: boolean
  isMulti?: IsMulti
  rules?: RegisterOptions<TFieldValues, TName>
  selectProps?: Partial<Omit<GrandSelectProps<Option, IsMulti>, 'ref'>>
  control?: Control<TFieldValues>
}

export const GrandFormSelectField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false
>({
  name,
  label,
  description,
  tooltip,
  placeholder = 'Välj...',
  options,
  required,
  optional,
  disabled,
  isClearable = false,
  isSearchable = false,
  isLoading,
  isMulti,
  rules,
  control,
  selectProps: { styles, ...selectProps } = {}
}: GrandFormSelectFieldProps<TFieldValues, TName, Option, IsMulti>) => {
  return (
    <FormField
      control={control}
      name={name}
      optional={optional}
      required={required}
      rules={rules}
      render={({ field }) => {
        const getSelectedValue = (): IsMulti extends true ? Option[] : Option | null => {
          if (isMulti) {
            const fieldValues = (field.value as (string | number)[]) || []
            return options.filter((opt) => fieldValues.includes(opt.value)) as IsMulti extends true
              ? Option[]
              : Option | null
          }

          return (options.find((opt) => opt.value === field.value) || null) as IsMulti extends true
            ? Option[]
            : Option | null
        }

        const handleChange = (
          newValue: IsMulti extends true ? readonly Option[] : Option | null
        ) => {
          if (isMulti) {
            const values = (newValue as Option[]).map((opt) => opt.value)
            field.onChange(values)
          } else {
            field.onChange((newValue as Option | null)?.value ?? '')
          }
        }

        return (
          <FormFieldControl disabled={disabled}>
            {label ? <FormFieldLabel tooltip={tooltip}>{label}</FormFieldLabel> : null}

            <GrandSelect<Option, IsMulti>
              isClearable={isClearable}
              isDisabled={disabled}
              isLoading={isLoading}
              isMulti={isMulti}
              isSearchable={isSearchable}
              onBlur={field.onBlur}
              onChange={handleChange}
              options={options}
              placeholder={placeholder}
              styles={{
                container: (base) => ({
                  ...base,
                  width: '100%'
                }),
                ...styles
              }}
              value={getSelectedValue()}
              {...selectProps}
            />

            {description ? <FormDescription>{description}</FormDescription> : null}

            <FormMessage />
          </FormFieldControl>
        )
      }}
    />
  )
}
